import React, { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ProgressIcon from '../ProgressIcon';
import './NavigationDropdownItem.scss';

const NavigationItem = ({
  module,
  chapterIntroPage,
  progress,
  itemType,
  isParentContent = false,
  displayProgressIcon = false,
}) => {
  // 1. Destructure with safety
  const {
    acf = {},
    post_title,
    colorScheme,
    completed,
    completion,
  } = module || {};

  const {
    illustration_icon,
    chapter_number,
    lesson_number,
    topic_number,
    step_number,
    title,
    subtitle,
  } = acf;

  // 2. Centralized URL Generation
  const itemSlug = useMemo(() => {
    const defaultTopic = '01';
    // Convert topic to 0-based index for parts
    const topicIdx = topic_number ? parseInt(topic_number, 10) - 1 : 0;

    switch (itemType) {
      case 'module':
        return `/learn/chapter/${chapter_number}`;
      
      case 'lesson':
      case 'lesson-topics-nav':
        return `/learn/chapter/${chapter_number}/lessons/lesson/${lesson_number}/part/0`;
      
      case 'topic':
        return `/learn/chapter/${chapter_number}/lessons/lesson/${lesson_number}/part/${topicIdx}`;
      
      case 'discharge':
        return `/discharge/${step_number}/lessons/${topic_number || defaultTopic}/part/0`;
      
      case 'discharge-topics':
        return `/discharge/${step_number}/lessons/01/part/${topicIdx}`;
      
      case 'chapter-intro-page':
        return `/learn/chapter/${chapter_number}/pages/${chapterIntroPage?.post_name}`;
      
      default:
        return '/';
    }
  }, [itemType, chapter_number, lesson_number, topic_number, step_number, chapterIntroPage]);

  const isChapterIntroPage = itemType === 'chapter-intro-page';

  return (
    <NavLink
      to={itemSlug}
      className={({ isActive }) => classNames('dropdown-navigation-item', {
        'dropdown-navigation-item-chapter-intro-topic': isChapterIntroPage,
        'completed': completed,
        'active': isActive && !isParentContent,
      })}
    >
      {illustration_icon && displayProgressIcon && (
        <ProgressIcon
          colorScheme={colorScheme}
          percentage={progress || completion}
          imgSrc={illustration_icon}
        />
      )}

      <div
        className={classNames({
          'dropdown-navigation-item__container__parent-content': isParentContent,
          'dropdown-navigation-item__container': !isParentContent,
        })}
      >
        {/* Progress bullet for nested topics */}
        {(itemType === 'topic' || itemType === 'discharge-topics') && (
          <span className="dropdown-navigation-item__progress-indicator" />
        )}

        <div
          className={classNames({
            'dropdown-navigation-item__title-parent-content': isParentContent,
            'dropdown-navigation-item__title': !isParentContent && !isChapterIntroPage,
            'dropdown-navigation-item__title-chapter-intro-page': isChapterIntroPage,
          })}
        >
          {isChapterIntroPage 
            ? (chapterIntroPage?.title || chapterIntroPage?.post_title)
            : (title || post_title)
          }
        </div>

        {/* Subtitles only show for modules/lessons, not topics */}
        {!isChapterIntroPage && !['topic', 'discharge-topics'].includes(itemType) && (
          <div className="dropdown-navigation-item__chapter-number">
            {subtitle}
          </div>
        )}
      </div>
    </NavLink>
  );
};

NavigationItem.propTypes = {
  module: PropTypes.shape({
    acf: PropTypes.object,
    post_title: PropTypes.string,
    colorScheme: PropTypes.string,
    completed: PropTypes.bool,
    completion: PropTypes.number,
  }),
  chapterIntroPage: PropTypes.shape({
    post_name: PropTypes.string,
    title: PropTypes.string,
    post_title: PropTypes.string,
  }),
  progress: PropTypes.number,
  itemType: PropTypes.string.isRequired,
  isParentContent: PropTypes.bool,
  displayProgressIcon: PropTypes.bool,
};

export default React.memo(NavigationItem);
